import type { RefObject } from "preact";
import { useEffect, useRef } from "preact/hooks";

/**
 * Zero-width Unicode characters that may be present in the DOM
 * due to markdown rendering fixes (e.g. LRM prepended to lines
 * starting with HTML tags, or empty-line placeholders).
 * These should not appear in copied text.
 */
const ZERO_WIDTH_REGEX = /[\u200B-\u200F\u202A-\u202E\u2060-\u2069]/g;

/**
 * Sanitize a string by removing zero-width Unicode characters.
 */
export function sanitizeText(text: string): string {
    return text.replace(ZERO_WIDTH_REGEX, "");
}

interface UseCopySanitizerOptions {
    /** Only sanitize if the copy target is within this element (or the element itself) */
    containerRef?: RefObject<HTMLElement | null>;
}

/**
 * Sanitize all text nodes under an element by removing zero-width characters.
 */
function sanitizeTextNodes(container: HTMLElement): void {
    const walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT,
        null,
    );

    let node: Node | null;
    while ((node = walker.nextNode())) {
        const textNode = node as Text;
        const original = textNode.textContent || "";
        if (ZERO_WIDTH_REGEX.test(original)) {
            const sanitized = sanitizeText(original);
            // Only update if different (avoids unnecessary DOM writes)
            if (sanitized !== original) {
                textNode.textContent = sanitized;
            }
        }
    }

    ZERO_WIDTH_REGEX.lastIndex = 0; // Reset regex state
}

/**
 * Hook that sanitizes copied text by removing zero-width Unicode characters.
 *
 * Uses a two-pronged approach:
 * 1. MutationObserver — removes zero-width chars from DOM text nodes immediately
 *    after render, so the browser's native copy doesn't see them at all.
 * 2. Copy event listener — intercepts Ctrl+C and sanitizes via clipboardData
 *    (fallback for edge cases where observer didn't catch everything).
 */
export function useCopySanitizer(options: UseCopySanitizerOptions = {}) {
    const { containerRef } = options;

    // Track processed copy events to avoid double-processing when
    // execCommand('copy') triggers a secondary copy event.
    const processedEvents = useRef(new WeakSet<ClipboardEvent>()).current;

    useEffect(() => {
        const container = containerRef?.current;
        if (!container) return;

        // Narrowed type — safe to use as HTMLElement after null check above.
        const el: HTMLElement = container;

        /**
         * Remove zero-width characters from all text nodes in the container.
         */
        function sanitizeAll() {
            sanitizeTextNodes(el);
        }

        // Run immediately for initial render, then after each DOM change.
        sanitizeAll();

        const observer = new MutationObserver((mutations) => {
            let needsSanitize = false;
            for (const mutation of mutations) {
                if (
                    mutation.type === "childList" ||
                    mutation.type === "characterData"
                ) {
                    needsSanitize = true;
                    break;
                }
            }
            if (needsSanitize) sanitizeAll();
        });

        observer.observe(el, {
            childList: true,
            characterData: true,
            subtree: true,
        });

        /**
         * Handle the `copy` event at capturing phase.
         * This is a safety net for cases where zero-width chars slipped through
         * the MutationObserver (e.g., extremely fast copy after render).
         */
        function onCopy(event: ClipboardEvent) {
            // Skip if this is a re-triggered event from our own execCommand call
            if (processedEvents.has(event)) return;

            // Check that the copy target is within our container
            const target = event.target as Node | null;
            if (event.currentTarget !== el) {
                if (!target || !el.contains(target)) return;
            }

            let sanitized: string | null = null;

            // Strategy 1: Try clipboardData first (works on most browsers)
            const clipboardText = event.clipboardData?.getData("text/plain");
            if (clipboardText && clipboardText.length > 0) {
                sanitized = sanitizeText(clipboardText);
            }

            // Strategy 2: Extract selected text from DOM directly.
            // This is needed when Preact's synthetic events don't expose
            // clipboardData properly (common on Linux).
            if (!sanitized) {
                const sel = window.getSelection();
                if (sel && !sel.isCollapsed) {
                    let text = "";
                    for (let i = 0; i < sel.rangeCount; i++) {
                        try {
                            const range = sel.getRangeAt(i);
                            if (!el.contains(range.commonAncestorContainer))
                                continue;
                            text += "\n" + range.toString();
                        } catch {
                            /* skip */
                        }
                    }
                    text = text.trimStart();
                    if (text.length > 0) sanitized = sanitizeText(text);
                }
            }

            if (!sanitized || sanitized.length === 0) return;

            event.preventDefault();
            processedEvents.add(event);

            // Strategy A: Set via clipboardData if available
            if (event.clipboardData) {
                event.clipboardData.setData("text/plain", sanitized);
            }

            // Strategy B: Also write directly via Clipboard API as a fallback.
            if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(sanitized).catch(() => {});
            }
        }

        document.addEventListener("copy", onCopy, true);

        return () => {
            observer.disconnect();
            document.removeEventListener("copy", onCopy, true);
        };
    }, [containerRef]);
}
